<template>
  <div
    v-if="visible"
    ref="menu"
    class="dropdown-menu show position-fixed"
    :style="{ left: `${x}px`, top: `${y}px` }"
  >
    <button class="dropdown-item d-flex align-items-center" @click="showCoordinates">
      <span class="material-icons mr-1">my_location</span>
      {{ $t('plan_trail.map_context_menu.show_coordinates') }}
    </button>
    <button
      v-if="hoveredPoi"
      class="dropdown-item d-flex align-items-center"
      @click="addPoiToRoute"
    >
      <span class="material-icons mr-1">add_location_alt</span>
      {{ $t('plan_trail.map_context_menu.add_to_route') }}
    </button>
    <button
      v-if="hoveredPoi"
      class="dropdown-item d-flex align-items-center"
      @click="addPoiAsHighlight"
    >
      <span class="material-icons mr-1">star</span>
      {{ $t('plan_trail.map_context_menu.add_poi_as_hike_highlight') }}
    </button>
    <button class="dropdown-item d-flex align-items-center" @click="addCustomPOI">
      <span class="material-icons mr-1">star</span>
      {{ $t('plan_trail.map_context_menu.add_hike_highlight') }}
    </button>
    <button
      v-if="activeRouteHit && !hoveredMarker && !hoveredPoi"
      class="dropdown-item d-flex align-items-center"
      @click="addToRoute"
    >
      <span class="material-icons mr-1">add_location_alt</span>
      {{ $t('plan_trail.map_context_menu.add_to_route') }}
    </button>
    <button
      v-if="activeRouteHit && !hoveredMarker"
      class="dropdown-item d-flex align-items-center"
      @click="addIntermediateControlPoint"
    >
      <span class="material-icons mr-1">add_circle_outline</span>
      {{ $t('plan_trail.map_context_menu.add_intermediate_control_point') }}
    </button>
    <button
      v-if="inactiveRoute"
      class="dropdown-item d-flex align-items-center"
      @click="activateRoute"
    >
      <span class="material-icons mr-1">edit</span>
      {{ $t('plan_trail.map_context_menu.activate_route') }}
    </button>
    <button
      v-if="hoveredMarker && canSplitHere"
      class="dropdown-item d-flex align-items-center"
      @click="splitRoute"
    >
      <span class="material-icons mr-1">arrow_split</span>
      {{ $t('plan_trail.map_context_menu.split_route_here') }}
    </button>
    <button
      v-if="hoveredMarker"
      class="dropdown-item d-flex align-items-center"
      @click="addCustomCue"
    >
      <span class="material-icons mr-1">alt_route</span>
      {{ $t('plan_trail.map_context_menu.add_turn_by_turn_prompt') }}
    </button>
    <button
      v-if="hoveredConnector"
      class="dropdown-item d-flex align-items-center"
      @click="disconnectStages"
    >
      <span class="material-icons mr-1">link_off</span>
      {{ $t('plan_trail.map_context_menu.separate_days') }}
    </button>
    <button
      v-if="hoveredReconnectLink"
      class="dropdown-item d-flex align-items-center"
      @click="reconnectStages"
    >
      <span class="material-icons mr-1">link</span>
      {{ $t('plan_trail.map_context_menu.join_days') }}
    </button>
    <button
      v-if="hoveredMarker"
      class="dropdown-item d-flex align-items-center text-danger"
      @click="removeMarker"
    >
      <span class="material-icons mr-1">delete</span>
      {{ $t('plan_trail.map_context_menu.remove_control_point') }}
    </button>
  </div>

  <!-- Coordinate Modal -->
  <div v-if="showCoordModal" class="modal show d-block" tabindex="-1" @click="closeCoordModal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('plan_trail.map_context_menu.coordinates') }}</h5>
          <button type="button" class="btn-close" @click="closeCoordModal"></button>
        </div>
        <div class="modal-body">
          <div class="row mb-2">
            <div class="col-4 fw-bold">{{ $t('plan_trail.map_context_menu.latitude') }}</div>
            <div class="col-8 font-monospace">{{ coordinates.lat.toFixed(6) }}</div>
          </div>
          <div class="row">
            <div class="col-4 fw-bold">{{ $t('plan_trail.map_context_menu.longitude') }}</div>
            <div class="col-8 font-monospace">{{ coordinates.lng.toFixed(6) }}</div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeCoordModal">
            {{ $t('shared.cancel_2') }}
          </button>
          <button type="button" class="btn btn-primary" @click="copyCoordinates">
            {{ copied ? $t('common.copied') : $t('common.copy') }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showCoordModal" class="modal-backdrop show"></div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { poiDestinationAtPoint, poiHighlightInputAtPoint } from '../utils/poiAtPoint';
import { poiToHikeHighlight } from '../../../utils/helpers/PoiToHikeHighlightHelper';
import { findRouteLineHit } from '../utils/routeLineHit';
import { formatGoToLocationLatLon } from '../../../utils/helpers/goToLocationLatLon';

export default {
  name: 'MapContextMenu',
  data() {
    return {
      visible: false,
      x: 0,
      y: 0,
      coordinates: { lat: 0, lng: 0 },
      hoveredMarker: null,
      hoveredConnector: null,
      hoveredReconnectLink: null,
      inactiveRoute: null,
      activeRouteHit: null,
      hoveredPoi: null,
      hoveredPoiDestination: null,
      showCoordModal: false,
      copied: false
    };
  },
  computed: {
    ...mapState('planTrail', ['routes', 'activeRouteKey']),
    canSplitHere() {
      if (!this.hoveredMarker) return false;
      const { routeId, markerIndex } = this.hoveredMarker;
      const routeKey = Object.keys(this.routes).find(key => this.routes[key].id === routeId);
      if (!routeKey) return false;
      const route = this.routes[routeKey];
      if (!route || !route.markers) return false;
      return markerIndex > 0 && markerIndex < route.markers.length - 1;
    }
  },
  mounted() {
    // Listen for context menu events from the map
    // In Vue 3, we'll use a global event emitter or props/emit pattern
    window.addEventListener('map-context-menu', this.handleMapContextMenu);
    document.addEventListener('click', this.hideMenu);
    document.addEventListener('contextmenu', this.handleDocumentContextMenu);
  },
  beforeUnmount() {
    window.removeEventListener('map-context-menu', this.handleMapContextMenu);
    document.removeEventListener('click', this.hideMenu);
    document.removeEventListener('contextmenu', this.handleDocumentContextMenu);
  },
  methods: {
    ...mapActions('planTrail', ['removeMarkerById', 'splitRouteAtMarker', 'addMarkerAtCoordinates', 'insertMarkerAtPoint']),

    handleMapContextMenu(event) {
      this.showMenu(event.detail);
    },

    showMenu(event) {
      const { x, y, lngLat, features } = event;

      // Position the menu with a small offset from click location
      // This makes it feel more natural and prevents the menu from appearing under the cursor
      const offset = 2; // pixels
      this.x = x + offset;
      this.y = y + offset;
      this.coordinates = lngLat;

      // Check if a marker was clicked
      this.hoveredMarker = this.findMarkerAtPoint(features);

      // Check if a stage-connector handle was clicked
      this.hoveredConnector = this.findConnectorAtPoint(features);

      // Check if a floating (unconnected) stage-end marker was clicked —
      // mutually exclusive with hoveredConnector (a handle is one or the other)
      this.hoveredReconnectLink = this.hoveredConnector ? null : this.findReconnectAtPoint(features);

      // Check if a map POI was clicked
      this.hoveredPoi = poiHighlightInputAtPoint(features, lngLat);
      this.hoveredPoiDestination = poiDestinationAtPoint(features, lngLat);

      // Check if an inactive route line was clicked
      this.inactiveRoute = this.findInactiveRouteAtPoint(features);

      // Check if the active route's line was clicked
      this.activeRouteHit = this.activeRouteKey
        ? findRouteLineHit(features, this.routes, lngLat, this.activeRouteKey)
        : null;

      this.visible = true;
      window.mapContextMenuVisible = true; // Set global flag

      // Ensure menu stays within viewport
      this.$nextTick(() => {
        if (this.$refs.menu) {
          const rect = this.$refs.menu.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          // Adjust if menu would go off the right edge
          if (rect.right > viewportWidth) {
            this.x = x - rect.width - offset;
          }

          // Adjust if menu would go off the bottom edge
          if (rect.bottom > viewportHeight) {
            this.y = y - rect.height - offset;
          }
        }
      });
    },

    hideMenu() {
      this.visible = false;
      this.hoveredMarker = null;
      this.hoveredConnector = null;
      this.hoveredReconnectLink = null;
      this.inactiveRoute = null;
      this.activeRouteHit = null;
      this.hoveredPoi = null;
      window.mapContextMenuVisible = false; // Clear global flag
    },

    handleDocumentContextMenu(e) {
      // Prevent default context menu outside of map
      if (!e.target.closest('.mapboxgl-canvas')) {
        this.hideMenu();
      }
    },

    findMarkerAtPoint(features) {
      if (!features || features.length === 0) return null;

      // Look for marker features
      const markerFeature = features.find(f =>
        f.layer && f.layer.id && (
          f.layer.id.includes('-marker-') || f.layer.id === 'consolidated-routes-marker-layer'
        )
      );

      if (markerFeature && markerFeature.properties) {
        return {
          routeId: markerFeature.properties.routeId,
          markerIndex: markerFeature.properties.markerIndex,
          markerId: markerFeature.properties.markerId
        };
      }

      return null;
    },

    findConnectorAtPoint(features) {
      if (!features || features.length === 0) return null;

      // Connected stage handles carry a connectorId; floating stage-end
      // markers (empty connectorId) have nothing to disconnect
      const connectorFeature = features.find(f =>
        f.layer && f.layer.id === 'stage-connector-handle-layer' &&
        f.properties && f.properties.connectorId
      );

      if (!connectorFeature) return null;
      return {
        connectorId: connectorFeature.properties.connectorId,
        prevRouteKey: connectorFeature.properties.prevRouteKey,
        nextRouteKey: connectorFeature.properties.nextRouteKey
      };
    },

    findReconnectAtPoint(features) {
      if (!features || features.length === 0) return null;

      // Floating (unconnected) stage-end markers carry an empty connectorId
      // but still sit on the routeKey of the stage they end
      const floatingFeature = features.find(f =>
        f.layer && f.layer.id === 'stage-connector-handle-layer' &&
        f.properties && !f.properties.connectorId && f.properties.routeKey
      );
      if (!floatingFeature) return null;

      const routeKey = floatingFeature.properties.routeKey;
      const link = (this.$store.getters['planTrail/reconnectableStageLinks'] || [])
        .find(l => l.prevRouteKey === routeKey);
      if (!link) return null;

      return { prevRouteKey: link.prevRouteKey, nextRouteKey: link.nextRouteKey };
    },

    findInactiveRouteAtPoint(features) {
      if (!features || features.length === 0) return null;

      // Look for inactive route line features
      const inactiveRouteFeature = features.find(f =>
        f.layer && f.layer.id && (
          f.layer.id === 'consolidated-inactive-routes-lines' ||
          f.layer.id === 'consolidated-inactive-routes-borders'
        )
      );

      if (inactiveRouteFeature && inactiveRouteFeature.properties) {
        const routeKey = inactiveRouteFeature.properties.routeKey;
        const route = this.routes[routeKey];

        return {
          routeKey: routeKey,
          routeId: inactiveRouteFeature.properties.routeId,
          routeName: route?.name || this.$t('plan_trail.map_context_menu.unnamed_route')
        };
      }

      return null;
    },

    showCoordinates() {
      this.showCoordModal = true;
      this.hideMenu();
    },

    closeCoordModal() {
      this.showCoordModal = false;
      this.copied = false;
    },

    async copyCoordinates() {
      const text = formatGoToLocationLatLon(this.coordinates.lat, this.coordinates.lng);

      try {
        await navigator.clipboard.writeText(text);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy coordinates:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          this.copied = true;
          setTimeout(() => {
            this.copied = false;
          }, 2000);
        } catch (err2) {
          console.error('Fallback copy failed:', err2);
        }
        document.body.removeChild(textArea);
      }
    },

    async removeMarker() {
      if (!this.hoveredMarker) return;

      const { routeId, markerIndex } = this.hoveredMarker;

      // Find the route
      const routeKey = Object.keys(this.routes).find(key =>
        this.routes[key].id === routeId
      );

      if (routeKey) {
        await this.removeMarkerById({
          routeKey,
          markerIndex
        });
      }

      this.hideMenu();
    },

    async splitRoute() {
      if (!this.hoveredMarker) return;
      const { routeId, markerIndex } = this.hoveredMarker;
      await this.splitRouteAtMarker({ routeId, markerIndex });
      this.hideMenu();
    },

    async disconnectStages() {
      if (!this.hoveredConnector) return;
      const { prevRouteKey, nextRouteKey } = this.hoveredConnector;
      this.hideMenu();
      await this.$store.dispatch('planTrail/disconnectStages', { prevRouteKey, nextRouteKey });
    },

    async reconnectStages() {
      if (!this.hoveredReconnectLink) return;
      const { prevRouteKey, nextRouteKey } = this.hoveredReconnectLink;
      this.hideMenu();
      await this.$store.dispatch('planTrail/reconnectStages', { prevRouteKey, nextRouteKey });
    },

    /**
     * A cue belongs to a control point, not to a place on the map — it is written into the
     * turns of the segment that starts there, so the marker is what identifies it.
     */
    addCustomCue() {
      if (!this.hoveredMarker) return;
      const { routeId, markerIndex } = this.hoveredMarker;
      this.$emit('add-custom-cue', { routeId, markerIndex });
      this.hideMenu();
    },

    /** The same highlight the POI sheet's "Add hike highlight" makes. */
    addPoiAsHighlight() {
      if (!this.hoveredPoi) return;
      this.$store.commit('planTrail/addHikeHighlight', poiToHikeHighlight(this.hoveredPoi));
      this.$store.dispatch('planTrail/refreshHighlights');
      this.hideMenu();
    },

    addCustomPOI() {
      // Emit an event with the coordinates where the POI should be added
      this.$emit('add-custom-poi', {
        coordinates: this.coordinates,
        lngLat: this.coordinates
      });

      // You could also dispatch a Vuex action if preferred
      // this.$store.dispatch('planTrail/addCustomPOI', {
      //   coordinates: this.coordinates
      // });

      this.hideMenu();
    },

    /**
     * Same gate a left click on the map goes through: editing a route that references a
     * published hike asks for confirmation first.
     */
    async confirmCanEdit() {
      if (typeof window.canEditReference !== 'function' || window.canEditReference()) return true;
      if (typeof window.checkReferenceEditWarning !== 'function') return false;
      return !!(await window.checkReferenceEditWarning());
    },

    /** Extends the route from its last control point to the clicked spot on the line. */
    async addToRoute() {
      const hit = this.activeRouteHit;
      this.hideMenu();
      if (!hit || !(await this.confirmCanEdit())) return;
      const [lng, lat] = hit.point;
      await this.addMarkerAtCoordinates({ lng, lat });
    },

    /** Exactly what dragging the route's end onto the right-clicked POI does — see addDestinationToRoute. */
    async addPoiToRoute() {
      const destination = this.hoveredPoiDestination;
      const poi = this.hoveredPoi;
      this.hideMenu();
      if (!poi || !(await this.confirmCanEdit())) return;
      if (destination) {
        await this.$store.dispatch('planTrail/addDestinationToRoute', { destination });
      } else {
        await this.addMarkerAtCoordinates({ lng: poi.lng, lat: poi.lat });
      }
    },

    /** Splits the clicked segment with a new control point, as a left click on the line does. */
    async addIntermediateControlPoint() {
      const hit = this.activeRouteHit;
      this.hideMenu();
      if (!hit || !(await this.confirmCanEdit())) return;
      await this.insertMarkerAtPoint({
        point: hit.point,
        routeId: hit.routeId,
        insertIndex: hit.insertIndex,
        segmentIndex: hit.segmentIndex
      });
    },

    async activateRoute() {
      if (!this.inactiveRoute) return;

      const { routeId } = this.inactiveRoute;

      // Switch to the selected route
      await this.$store.dispatch('planTrail/switchToRoute', routeId);

      this.hideMenu();
    }
  }
};
</script>

<style scoped>
/* Override Bootstrap dropdown-menu positioning since we handle it manually */
.dropdown-menu {
  position: fixed !important;
  margin: 0;
}

.dropdown-item .material-icons{
  font-size: 20px;
}

</style>
